import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, AlertCircle, Sparkles, Copy, Check, Pencil, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Header from "../components/dashboard/Header";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { fmtCurrency, fmtPeriod } from "../lib/format";

const KEY_FIELDS = ["total_revenue", "total_expenses", "net_ordinary_income", "net_income"];

const FORECAST_GROUPS = [
  { title: "Revenue", fields: [
    ["member_revenue", "Member Revenue"], ["service_revenue", "Service Revenue"],
    ["other_revenue", "Other Revenue"], ["management_revenue", "Management Revenue"],
  ] },
  { title: "Operating Expenses", fields: [
    ["rent_expense", "Rent Expense"], ["premise_expense", "Premise Expense"],
    ["member_services_expense", "Member Services"], ["salary_wages", "Salary & Wages"],
    ["general_expenses", "General Expenses"], ["marketing_sales_expense", "Marketing & Sales"],
  ] },
  { title: "Below the line", fields: [
    ["corp_expense_allocation", "Corporate Allocation"], ["legal_bad_debt", "Legal / Bad Debt"],
  ] },
];
const FORECAST_LEAVES = FORECAST_GROUPS.flatMap((g) => g.fields.map((f) => f[0]));

function ForecastEditor({ location, period, seed, onClose, onSaved }) {
  const [vals, setVals] = useState(() => {
    const o = {};
    FORECAST_LEAVES.forEach((f) => { o[f] = seed?.[f] != null ? String(seed[f]) : ""; });
    return o;
  });
  const [saving, setSaving] = useState(false);
  const num = (f) => parseFloat(vals[f]) || 0;
  const totalRev = num("member_revenue") + num("service_revenue") + num("other_revenue") + num("management_revenue");
  const totalExp = num("rent_expense") + num("premise_expense") + num("member_services_expense") + num("salary_wages") + num("general_expenses") + num("marketing_sales_expense");
  const noi = totalRev - totalExp;
  const ni = noi - num("corp_expense_allocation") - num("legal_bad_debt");

  const save = async () => {
    setSaving(true);
    try {
      const payload = { location, period };
      FORECAST_LEAVES.forEach((f) => { payload[f] = num(f); });
      await api.put("/budget/forecast", payload);
      toast.success(`Forecast saved for ${fmtPeriod(period)}`);
      onSaved();
    } catch (e) {
      toast.error("Couldn't save forecast", { description: e.response?.data?.detail });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-ink/50 backdrop-blur-sm" data-testid="forecast-editor" onClick={onClose}>
      <div className="bg-brand-page w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-brand-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-brand-page border-b border-brand-border px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-brand-tertiary">Editable Forecast</p>
            <h3 className="font-serif text-xl text-brand-ink">{fmtPeriod(period)} projection</h3>
          </div>
          <button onClick={onClose} data-testid="forecast-close" className="text-brand-muted hover:text-brand-ink"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-6">
          <p className="text-xs text-brand-muted">Adjust any line for this not-yet-posted month. Totals recalculate live and feed the full-year forecast. The original plan stays as your budget for variance.</p>
          {FORECAST_GROUPS.map((g) => (
            <div key={g.title}>
              <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-2">{g.title}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {g.fields.map(([f, label]) => (
                  <label key={f} className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-brand-muted">{label}</span>
                    <input type="number" value={vals[f]} data-testid={`forecast-input-${f}`}
                      onChange={(e) => setVals((p) => ({ ...p, [f]: e.target.value }))}
                      className="w-32 text-right font-mono bg-brand-surface border border-brand-border rounded px-2 py-1.5 focus:outline-none focus:border-brand-primary" />
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="border-t border-brand-border pt-4 space-y-2 text-sm">
            {[["Total Revenue", totalRev], ["Total Expenses", totalExp], ["Net Ordinary Income", noi]].map(([l, v]) => (
              <div key={l} className="flex justify-between"><span className="text-brand-muted">{l}</span><span className="font-mono text-brand-ink">{fmtCurrency(v)}</span></div>
            ))}
            <div className="flex justify-between font-medium"><span className="text-brand-ink">Net Income</span><span className="font-mono text-brand-ink" data-testid="forecast-net-income">{fmtCurrency(ni)}</span></div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-brand-page border-t border-brand-border px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-brand-muted hover:text-brand-ink">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="forecast-save" className="px-5 py-2 text-sm bg-brand-primary text-brand-page hover:bg-brand-primaryHover disabled:opacity-60">{saving ? "Saving…" : "Save forecast"}</button>
        </div>
      </div>
    </div>
  );
}

function VarBadge({ row, pts }) {
  if (row.abs == null || row.favorable == null) return <span className="text-brand-tertiary text-xs">—</span>;
  const good = row.favorable;
  const Icon = row.abs >= 0 ? ArrowUpRight : ArrowDownRight;
  const val = pts != null ? `${pts >= 0 ? "+" : ""}${pts} pts` : `${row.abs >= 0 ? "+" : ""}${fmtCurrency(row.abs)}`;
  const pct = row.pct != null && pts == null ? ` (${row.pct >= 0 ? "+" : ""}${row.pct}%)` : "";
  return (
    <span className={`inline-flex items-center gap-1 font-mono text-xs ${good ? "text-brand-pos" : "text-brand-neg"}`}>
      <Icon className="h-3 w-3" />{val}{pct}
    </span>
  );
}

function CompareCard({ row, index, highlight, isForecast }) {
  const pending = row.actual == null;
  const showVar = !pending;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: index * 0.05 }}
      data-testid={`bva-card-${row.field}`}
      className={`p-6 flex flex-col justify-between min-h-[140px] ${highlight ? "bg-brand-ink text-brand-page" : "bg-brand-surface"}`}>
      <div className="flex items-center justify-between">
        <p className={`text-xs uppercase tracking-[0.15em] ${highlight ? "text-brand-page/60" : "text-brand-tertiary"}`}>{row.label}</p>
        {showVar && <VarBadge row={row} />}
      </div>
      <div className="mt-4">
        <p className={`font-mono text-2xl lg:text-3xl tracking-tight ${highlight ? "text-brand-page" : "text-brand-ink"}`}>
          {pending ? fmtCurrency(row.budget) : fmtCurrency(row.actual)}
        </p>
        <p className={`text-[11px] mt-1 ${highlight ? "text-brand-page/50" : "text-brand-tertiary"}`}>
          {pending ? "Budget · actuals pending" : isForecast ? `Forecast · budget ${fmtCurrency(row.budget)}` : `Budget ${fmtCurrency(row.budget)}`}
        </p>
      </div>
    </motion.div>
  );
}

function VarianceTable({ rows, title, subtitle, sortByMiss, testid, actualLabel = "Actual" }) {
  const data = sortByMiss
    ? [...rows].filter((r) => r.abs != null).sort((a, b) => Math.abs(b.abs) - Math.abs(a.abs))
    : rows;
  return (
    <div className="bg-brand-surface border border-brand-border" data-testid={testid}>
      <div className="px-6 py-4 border-b border-brand-border">
        <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">{title}</p>
        {subtitle && <p className="text-sm text-brand-muted mt-0.5">{subtitle}</p>}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-brand-tertiary text-[10px] uppercase tracking-[0.15em]">
            <th className="text-left font-normal px-6 py-3">Line Item</th>
            <th className="text-right font-normal px-4 py-3">Budget</th>
            <th className="text-right font-normal px-4 py-3">{actualLabel}</th>
            <th className="text-right font-normal px-6 py-3">Variance</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r) => {
            const bold = ["total_revenue", "total_expenses", "net_ordinary_income", "net_income"].includes(r.field);
            return (
              <tr key={r.field} data-testid={`bva-row-${r.field}`}
                className={`border-t border-brand-border ${bold ? "font-medium" : ""}`}>
                <td className={`px-6 py-3 ${bold ? "text-brand-ink" : "text-brand-muted"}`}>{r.label}</td>
                <td className="px-4 py-3 text-right font-mono text-brand-muted">{r.budget == null ? "—" : fmtCurrency(r.budget)}</td>
                <td className="px-4 py-3 text-right font-mono text-brand-ink">{r.actual == null ? "—" : fmtCurrency(r.actual)}</td>
                <td className="px-6 py-3 text-right"><VarBadge row={r} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Commentary({ commentary, periodLabel }) {
  const [copied, setCopied] = useState(false);
  if (!commentary || (!commentary.headline && !commentary.points?.length)) return null;
  const lines = [commentary.headline, ...(commentary.points || [])].filter(Boolean);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`${periodLabel} — Budget vs Actual\n\n` + lines.map((l) => `• ${l}`).join("\n"));
      setCopied(true);
      toast.success("Commentary copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy"); }
  };
  return (
    <div className="bg-brand-ink text-brand-page p-6" data-testid="bva-commentary">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-brand-gold" />
          <p className="text-[10px] uppercase tracking-[0.2em] text-brand-page/60">Variance Commentary</p>
        </div>
        <button onClick={copy} data-testid="bva-copy-commentary"
          className="inline-flex items-center gap-1.5 text-[11px] text-brand-page/70 hover:text-brand-page border border-brand-page/20 hover:border-brand-page/50 rounded-full px-3 py-1.5 transition-colors">
          {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copied ? "Copied" : "Copy"}
        </button>
      </div>
      {commentary.headline && <p className="font-serif text-xl lg:text-2xl font-light mt-3 leading-snug">{commentary.headline}</p>}
      {commentary.points?.length > 0 && (
        <ul className="mt-4 space-y-2">
          {commentary.points.map((p, i) => (
            <li key={i} className="flex gap-2.5 text-sm text-brand-page/85">
              <span className="text-brand-gold mt-0.5">—</span><span>{p}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function Budget() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState(null);
  const [location, setLocation] = useState("all");
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingForecast, setEditingForecast] = useState(false);

  const loadMeta = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/budget/meta");
      setMeta(data);
      setPeriod((p) => p || data.months[0]);
    } catch (e) { console.error("Failed to load budget meta", e); }
  }, [isAdmin]);

  const load = useCallback(async () => {
    if (!isAdmin || !period) return;
    setLoading(true);
    try {
      const { data } = await api.get("/budget/compare", { params: { location, period } });
      setData(data);
    } catch (e) {
      console.error("Failed to load budget comparison", e);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, location, period]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => { load(); }, [load]);

  if (user && !["admin", "regional"].includes(user.role)) return <Navigate to="/" replace />;
  if (!meta) {
    return (
      <div className="min-h-screen bg-brand-page relative z-10"><Header />
        <div className="flex items-center justify-center h-[70vh]"><div className="h-10 w-10 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
      </div>
    );
  }

  const locLabel = meta.locations.find((l) => l.name === location)?.label || location;
  const monthly = data?.monthly;
  const h2 = data?.h2;
  const annual = data?.annual;
  const useForecast = !!(monthly?.editable && monthly?.has_forecast);
  const displayRows = useForecast ? monthly.forecast_rows : monthly?.rows;
  const keyRows = (displayRows || []).filter((r) => KEY_FIELDS.includes(r.field));
  const budgetByField = {};
  (monthly?.rows || []).forEach((r) => { budgetByField[r.field] = r.budget; });
  const forecastSeed = monthly?.forecast || budgetByField;
  const resetForecast = async () => {
    try {
      await api.delete(`/budget/forecast/${encodeURIComponent(location)}/${period}`);
      toast.success("Forecast reset to plan");
      load();
    } catch (e) { toast.error("Couldn't reset forecast", { description: e.response?.data?.detail }); }
  };
  const forecastNI = (annual?.rows || []).find((r) => r.field === "net_income");
  const forecastRev = (annual?.rows || []).find((r) => r.field === "total_revenue");
  const annualMonths = annual?.actual_months?.length || 0;

  const fieldSelect = "appearance-none bg-brand-surface border border-brand-border hover:border-brand-borderStrong text-sm text-brand-ink rounded-full px-4 py-2.5 focus:outline-none focus:border-brand-primary transition-colors cursor-pointer min-w-[180px]";

  return (
    <div className="min-h-screen bg-brand-page relative z-10">
      <Header />
      <main className="max-w-[1600px] mx-auto px-6 lg:px-12 py-10 space-y-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-brand-tertiary">Admin · Planning</p>
            <h1 className="font-serif text-4xl lg:text-5xl font-light tracking-tight text-brand-ink mt-2">Budget vs Actual</h1>
            <p className="text-sm text-brand-muted mt-1">{locLabel} · projections for Jul–Dec 2026 measured against actuals as they post.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Location</label>
              <select value={location} onChange={(e) => setLocation(e.target.value)} className={fieldSelect} data-testid="bva-location">
                {meta.locations.map((l) => <option key={l.name} value={l.name}>{l.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.15em] text-brand-tertiary mb-1.5">Month</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} className={fieldSelect} data-testid="bva-period">
                {meta.months.map((m) => <option key={m} value={m}>{fmtPeriod(m)}</option>)}
              </select>
            </div>
          </div>
        </div>

        {!data ? (
          <div className="py-20 flex justify-center"><div className="h-8 w-8 rounded-full border-2 border-brand-border border-t-brand-primary animate-spin" /></div>
        ) : (
          <motion.div key={`${location}-${period}`} initial={{ opacity: 0 }} animate={{ opacity: loading ? 0.5 : 1 }} className="space-y-10">
            {/* Section 1: selected month */}
            <section className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-serif text-2xl text-brand-ink">{fmtPeriod(period)} — Month in focus</h2>
                <div className="flex items-center gap-2">
                  {!monthly.has_actual && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-brand-gold border border-brand-gold/40 rounded-full px-3 py-1" data-testid="bva-pending">
                      <AlertCircle className="h-3.5 w-3.5" /> Actuals pending — upload {fmtPeriod(period)}'s workbook
                    </span>
                  )}
                  {monthly.editable && isAdmin && (
                    <>
                      <button onClick={() => setEditingForecast(true)} data-testid="bva-edit-forecast"
                        className="inline-flex items-center gap-1.5 text-xs border border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-brand-page rounded-full px-3 py-1.5 transition-colors">
                        <Pencil className="h-3.5 w-3.5" /> {monthly.has_forecast ? "Edit forecast" : "Adjust forecast"}
                      </button>
                      {monthly.has_forecast && (
                        <button onClick={resetForecast} data-testid="bva-reset-forecast"
                          className="inline-flex items-center gap-1.5 text-xs text-brand-muted hover:text-brand-ink border border-brand-border rounded-full px-3 py-1.5 transition-colors">
                          <RotateCcw className="h-3.5 w-3.5" /> Reset to plan
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
              {useForecast && (
                <div className="flex items-center gap-2 text-xs text-brand-primary bg-brand-primary/5 border border-brand-primary/20 rounded-lg px-4 py-2.5" data-testid="bva-forecast-active">
                  <Pencil className="h-3.5 w-3.5 shrink-0" />
                  Showing your adjusted forecast for {fmtPeriod(period)} vs the original plan. This forecast feeds the full-year outlook below.
                </div>
              )}
              {monthly.move_out_impact > 0 && (
                <div className="flex items-center gap-2 text-xs text-brand-neg bg-brand-neg/5 border border-brand-neg/20 rounded-lg px-4 py-2.5" data-testid="monthly-moveout-impact">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  {fmtCurrency(monthly.move_out_impact)}/mo of this month's plan is at risk — companies that have given notice will have vacated by {fmtPeriod(period)}.
                </div>
              )}
              {monthly.new_business_impact > 0 && (
                <div className="flex items-center gap-2 text-xs text-brand-pos bg-brand-pos/5 border border-brand-pos/20 rounded-lg px-4 py-2.5" data-testid="monthly-newbiz-impact">
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                  {fmtCurrency(monthly.new_business_impact)}/mo of new recurring business is layered onto this month's plan from deals closed to date.
                </div>
              )}
              {monthly.commentary && <Commentary commentary={monthly.commentary} periodLabel={fmtPeriod(period)} />}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-brand-border border border-brand-border">
                {keyRows.map((r, i) => <CompareCard key={r.field} row={r} index={i} highlight={r.field === "net_income"} isForecast={useForecast} />)}
              </div>
              {monthly.occupancy && (
                <div className="bg-brand-surface border border-brand-border px-6 py-4 flex items-center justify-between" data-testid="bva-occupancy">
                  <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Occupancy</p>
                  <div className="flex items-center gap-6 text-sm">
                    <span className="text-brand-muted">Budget <span className="font-mono text-brand-ink">{monthly.occupancy.budget != null ? `${(monthly.occupancy.budget * 100).toFixed(1)}%` : "—"}</span></span>
                    <span className="text-brand-muted">Actual <span className="font-mono text-brand-ink">{monthly.occupancy.actual != null ? `${(monthly.occupancy.actual * 100).toFixed(1)}%` : "—"}</span></span>
                    {monthly.occupancy.abs_pts != null && (
                      <span className={`font-mono ${monthly.occupancy.favorable ? "text-brand-pos" : "text-brand-neg"}`}>{monthly.occupancy.abs_pts >= 0 ? "+" : ""}{monthly.occupancy.abs_pts} pts</span>
                    )}
                  </div>
                </div>
              )}
              {monthly.has_actual ? (
                <VarianceTable rows={monthly.rows} sortByMiss testid="bva-variance-table"
                  title="What was off — line-item variance" subtitle="Sorted by biggest gap between budget and actual." />
              ) : useForecast ? (
                <VarianceTable rows={monthly.forecast_rows} testid="bva-forecast-table" actualLabel="Forecast"
                  title="Your forecast vs original plan" subtitle="Every line as you've revised it for this month. Original plan stays the budget." />
              ) : (
                <VarianceTable rows={monthly.rows} testid="bva-budget-table"
                  title="Budgeted line items" subtitle="The full-P&L plan for this month. Variance appears once actuals are uploaded." />
              )}
            </section>

            {/* Section 2: H2 pacing */}
            <section className="space-y-4">
              <h2 className="font-serif text-2xl text-brand-ink">H2 2026 pacing (Jul–Dec)</h2>
              {h2.elapsed > 0 ? (
                <VarianceTable rows={h2.to_date_rows} sortByMiss testid="bva-h2-table"
                  title={`Actual vs budget · ${h2.elapsed} of ${h2.total_months} months posted`}
                  subtitle="How the uploaded months are tracking against the second-half plan." />
              ) : (
                <div className="bg-brand-surface border border-brand-border px-6 py-8 text-center" data-testid="bva-h2-empty">
                  <p className="text-sm text-brand-muted">No H2 months uploaded yet. The full Jul–Dec plan totals
                    <span className="font-mono text-brand-ink"> {fmtCurrency(h2.full_budget.total_revenue)}</span> revenue and
                    <span className="font-mono text-brand-ink"> {fmtCurrency(h2.full_budget.net_income)}</span> net income.</p>
                  <p className="text-xs text-brand-tertiary mt-1">Pacing vs plan will appear here starting with July's upload.</p>
                </div>
              )}
            </section>

            {/* Section 3: full year */}
            <section className="space-y-4">
              <h2 className="font-serif text-2xl text-brand-ink">Full-Year 2026 outlook</h2>
              <p className="text-sm text-brand-muted -mt-2">Revised forecast = {annualMonths} actual month{annualMonths === 1 ? "" : "s"} + remaining months at {annual.override_months?.length ? "your adjusted forecast where set, otherwise plan" : "plan"}. Shows how current performance reshapes the year.</p>
              {annual.override_months?.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-brand-primary bg-brand-primary/5 border border-brand-primary/20 rounded-lg px-4 py-2.5 -mt-1" data-testid="annual-override-note">
                  <Pencil className="h-3.5 w-3.5 shrink-0" />
                  Adjusted forecasts applied for {annual.override_months.map((m) => fmtPeriod(m)).join(", ")}.
                </div>
              )}
              {annual.move_out_drag > 0 && (
                <div className="flex items-center gap-2 text-xs text-brand-neg bg-brand-neg/5 border border-brand-neg/20 rounded-lg px-4 py-2.5 -mt-1" data-testid="annual-moveout-drag">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  Forecast dampened by {fmtCurrency(annual.move_out_drag)} of revenue from upcoming move-outs (applied to the months after each company vacates).
                </div>
              )}
              {annual.new_business_lift > 0 && (
                <div className="flex items-center gap-2 text-xs text-brand-pos bg-brand-pos/5 border border-brand-pos/20 rounded-lg px-4 py-2.5 -mt-1" data-testid="annual-newbiz-lift">
                  <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
                  Forecast lifted by {fmtCurrency(annual.new_business_lift)} of new recurring revenue from closed deals (applied to projected months from each deal's close date).
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-brand-border border border-brand-border">
                {[forecastRev, forecastNI].filter(Boolean).map((r, i) => (
                  <div key={r.field} className="bg-brand-surface p-6" data-testid={`bva-annual-${r.field}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary">Forecast {r.label}</p>
                      <VarBadge row={r} />
                    </div>
                    <p className="font-mono text-3xl text-brand-ink mt-3">{fmtCurrency(r.actual)}</p>
                    <p className="text-[11px] text-brand-tertiary mt-1">Original budget {fmtCurrency(r.budget)}</p>
                  </div>
                ))}
              </div>
              <VarianceTable rows={annual.rows} testid="bva-annual-table"
                title="Full-year forecast vs original budget" subtitle="Every line, revised for actuals to date." />
            </section>
          </motion.div>
        )}
      </main>
      {editingForecast && monthly?.editable && (
        <ForecastEditor location={location} period={period} seed={forecastSeed}
          onClose={() => setEditingForecast(false)}
          onSaved={() => { setEditingForecast(false); load(); }} />
      )}
    </div>
  );
}
